import { WatchQueryFetchPolicy } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import useEnhancedQuery from '@/modules/common/application/use-enhanced-query';
import { SET_SELECTED_YEAR, UPDATE_COLLECTION_YEARS } from '@/services/graphql/mutations';
import { GET_YEARS } from '@/services/graphql/queries';

import {
  SetSelectedYearMutation,
  SetSelectedYearMutationVariables,
  UpdateYearsMutation,
  UpdateYearsMutationVariables,
  YearsQuery,
} from '../types';
import { useMessage } from './context/message';
import { useUser } from './context/user';
import useEnhancedMutation from './use-enhanced-mutation';

export const useYears = (
  fetchPolicy: WatchQueryFetchPolicy | undefined = 'cache-first',
  showSuccessMessage = true
) => {
  const { t } = useTranslation();
  const { setErrorMessage, setSuccessMessage } = useMessage();
  const { user } = useUser();

  const selectedUserYear = user?.selectedYear ?? 2022;

  const { loading: yearsLoading, data } = useEnhancedQuery<YearsQuery>(GET_YEARS, {
    fetchPolicy,
    onError: () => setErrorMessage(t('messages.getCollectionYears.error')),
  });

  const [setSelectedYear] = useEnhancedMutation<
    SetSelectedYearMutation,
    SetSelectedYearMutationVariables
  >(SET_SELECTED_YEAR, {
    update: (cache, { data }) => {
      if (!data) {
        return;
      }
      cache.modify({
        id: cache.identify({ id: data.setSelectedYear.id, __typename: 'User' }),
        fields: {
          selectedYear: () => data.setSelectedYear.selectedYear,
        },
      });
    },
  });

  const [updateYears, { loading: updateYearsLoading }] = useEnhancedMutation<
    UpdateYearsMutation,
    UpdateYearsMutationVariables
  >(UPDATE_COLLECTION_YEARS, {
    onCompleted: () => {
      if (showSuccessMessage) {
        setSuccessMessage(t('messages.updateCollectionYears.success'));
      }
    },
    onError: () => setErrorMessage(t('messages.updateCollectionYears.error')),
    update: (cache, { data }) => {
      if (data?.updateYears) {
        const previousData = cache.readQuery<{ years: number[] }>({
          query: GET_YEARS,
        });
        cache.writeQuery({
          query: GET_YEARS,
          data: {
            years: [...(previousData?.years || []), ...data.updateYears],
          },
        });
      }
    },
  });

  const sortedYears = data?.years
    ? (JSON.parse(JSON.stringify(data?.years)) as number[]).sort((a, b) => a - b)
    : null;

  const handleSetSelectedYear = async (year: number) => {
    await setSelectedYear({
      variables: { year },
    });
  };

  const handleUpdateYears = async (years: number[]) => {
    await updateYears({
      variables: { years },
    });
  };

  return {
    sortedYears,
    yearsLoading,
    selectedYear: selectedUserYear,
    handleSetSelectedYear,
    handleUpdateYears,
    updateYearsLoading,
  };
};
